import { Filter as FilterIcon, Search } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';

import { CatalogFilterInfoBanner } from '~/components/common/catalog-filter-info-banner';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Switch } from '~/components/ui/switch';
import type {
  CurationFilterDefinition,
  ProductFilterValues,
} from '~/features/catalog-operations/types/catalog-operations.types';

// Re-exported for the legacy callers — the type is still defined locally so
// existing imports keep compiling.
export type ProductFilters = ProductFilterValues;

const CB = 'h-5 w-5 rounded';

/** Auto-enable the option search box when a multi_select carries more than
 *  this many options — small lists don't benefit from a filter. */
const OPTION_SEARCH_THRESHOLD = 15;

/** Ceiling on option rows rendered at once per section.
 *
 *  A catalog-derived field (product type, category, colour) can carry several
 *  hundred distinct values, and mounting every one of them as a Radix checkbox
 *  is what made opening this drawer slow — for a list only ~10 rows tall. The
 *  search box above the list reaches anything beyond the cap, and a hint below
 *  it says how many are hidden, so nothing becomes unreachable. */
const OPTION_RENDER_LIMIT = 100;

/** Shared empty array so an untouched multi_select keeps one `selected`
 *  identity across renders and its memoised section can bail out. */
const NO_SELECTION: string[] = [];

/** Placeholder sections rendered while a caller derives its filter options. */
const SKELETON_SECTIONS = ['a', 'b', 'c'];

/** Narrows a draft value to the string a text/range input shows. */
function stringValue(value: ProductFilterValues[string] | undefined): string {
  return typeof value === 'string' ? value : '';
}

/** Returns the visible-count for a filter value — used to drive the
 *  "N Selected" header and the Apply button badge. */
function countActive(value: ProductFilterValues[string] | undefined): number {
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'string') return value.trim() ? 1 : 0;
  return value ? 1 : 0;
}

interface SectionHeaderProps {
  label: string;
  count: number;
}

function SectionHeader({ label, count }: SectionHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold">{label}</span>
      {count > 0 && (
        <span
          data-testid="products-filter-drawer-section-count"
          className="text-primary text-xs font-semibold"
        >
          {String(count)} Selected
        </span>
      )}
    </div>
  );
}

interface MultiSelectSectionProps {
  definition: CurationFilterDefinition;
  selected: string[];
  /** Takes the filter key as well as the value so the parent can hand down one
   *  stable callback for every section (see `handleToggleMulti`). */
  onToggle: (key: string, value: string) => void;
}

/** Checkbox list for a `multi_select` filter. Surfaces a search input when
 *  the option list crosses `OPTION_SEARCH_THRESHOLD`, and renders at most
 *  `OPTION_RENDER_LIMIT` rows at a time.
 *
 *  Memoised: ticking a box in one section re-renders that section only, rather
 *  than every option row in the drawer. */
const MultiSelectSection = memo(function MultiSelectSection({
  definition,
  selected,
  onToggle,
}: MultiSelectSectionProps) {
  const [query, setQuery] = useState('');
  const showSearch = definition.options.length > OPTION_SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    if (!query.trim()) return definition.options;
    const q = query.toLowerCase();
    return definition.options.filter((o) => o.toLowerCase().includes(q));
  }, [definition.options, query]);

  // Set lookup, not `selected.includes` — the latter is O(n) per option, so a
  // few hundred options against a few hundred picks is quadratic on every render.
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const visible =
    filtered.length > OPTION_RENDER_LIMIT ? filtered.slice(0, OPTION_RENDER_LIMIT) : filtered;
  const hiddenCount = filtered.length - visible.length;

  return (
    <div data-testid={`products-filter-drawer-section-${definition.key}`} className="space-y-2.5">
      <SectionHeader label={definition.label} count={selected.length} />
      <div className="bg-surface-stat-card space-y-2 rounded-xl p-3">
        {showSearch && (
          <div className="relative">
            <Search
              className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              data-testid={`products-filter-drawer-${definition.key}-search-input`}
              placeholder={`Search ${definition.label.toLowerCase()}`}
              aria-label={`Search ${definition.label.toLowerCase()}`}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              className="h-9 rounded-xl bg-white pl-9 text-sm shadow-none"
            />
          </div>
        )}
        <div className="max-h-[300px] space-y-1 overflow-y-scroll">
          {visible.map((option) => {
            const id = `${definition.key}-${option.replace(/\s+/g, '-').toLowerCase()}`;
            return (
              <div key={option} className="flex items-center gap-3 py-1.5">
                <Checkbox
                  data-testid={`products-filter-drawer-option-${id}-checkbox`}
                  id={id}
                  className={CB}
                  checked={selectedSet.has(option)}
                  onCheckedChange={() => {
                    onToggle(definition.key, option);
                  }}
                />
                <Label htmlFor={id} className="flex-1 cursor-pointer text-sm">
                  {option}
                </Label>
                {definition.optionCounts?.[option] !== undefined && (
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {definition.optionCounts[option].toLocaleString()}
                  </span>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p
              data-testid={`products-filter-drawer-${definition.key}-no-matches`}
              className="text-muted-foreground py-2 text-xs"
            >
              No matches
            </p>
          )}
          {hiddenCount > 0 && (
            <p className="text-muted-foreground border-t pt-2 text-xs">
              {String(hiddenCount)} more — search to narrow the list.
            </p>
          )}
          {definition.optionsTruncated && (
            <p
              data-testid={`products-filter-drawer-${definition.key}-truncated`}
              className="text-muted-foreground border-t pt-2 text-xs"
            >
              Showing the most common values only.
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

interface TextSectionProps {
  definition: CurationFilterDefinition;
  value: string;
  onChange: (value: string) => void;
  /** E2E `data-testid` prefix; the section is `${testId}-section-<key>`. */
  testId?: string;
}

/** Text input for `text` (exact) and `text_contains` (ILIKE) filter types. */
function TextSection({
  definition,
  value,
  onChange,
  testId = 'products-filter-drawer',
}: TextSectionProps) {
  const placeholder =
    definition.type === 'text_contains'
      ? `Search ${definition.label.toLowerCase()}`
      : `Enter ${definition.label.toLowerCase()}`;
  return (
    <div data-testid={`${testId}-section-${definition.key}`} className="space-y-2.5">
      <SectionHeader label={definition.label} count={countActive(value)} />
      <div className="bg-surface-stat-card rounded-xl p-3">
        <Input
          data-testid={`${testId}-${definition.key}-input`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          className="h-9 rounded-xl bg-white text-sm shadow-none"
        />
      </div>
    </div>
  );
}

/** Suffixes the run-scoped listing reads for an inclusive range bound. */
const MIN_SUFFIX = '__min';
const MAX_SUFFIX = '__max';

/** End-of-day time appended to a date-only upper bound, so "to 2026-10-28"
 *  includes that day rather than stopping at its midnight. */
const END_OF_DAY = 'T23:59:59.999999';

function formatBound(
  type: CurationFilterDefinition['type'],
  bound: number | string | null | undefined,
) {
  if (bound === null || bound === undefined) return '';
  if (type === 'date_range') return String(bound).slice(0, 10);
  return typeof bound === 'number' ? String(Math.round(bound * 100) / 100) : bound;
}

interface RangeSectionProps {
  definition: CurationFilterDefinition;
  /** Draft values for `<key>__min` / `<key>__max` (empty string = unset). */
  minValue: string;
  maxValue: string;
  /** Writes one bound; `paramKey` is the full `<key>__min|__max` query param. */
  onChange: (paramKey: string, value: string) => void;
}

/** Min / max inputs for `numeric_range` and `date_range` filters. The run's
 *  own bounds are shown as placeholders and as the date pickers' limits. */
function RangeSection({ definition, minValue, maxValue, onChange }: RangeSectionProps) {
  const isDate = definition.type === 'date_range';
  const lower = formatBound(definition.type, definition.min);
  const upper = formatBound(definition.type, definition.max);
  const minKey = `${definition.key}${MIN_SUFFIX}`;
  const maxKey = `${definition.key}${MAX_SUFFIX}`;
  const inputClass = 'h-9 rounded-xl bg-white text-sm shadow-none';
  const activeCount = (minValue ? 1 : 0) + (maxValue ? 1 : 0);
  return (
    <div data-testid={`products-filter-drawer-section-${definition.key}`} className="space-y-2.5">
      <SectionHeader label={definition.label} count={activeCount} />
      <div className="bg-surface-stat-card flex items-center gap-2 rounded-xl p-3">
        <Input
          data-testid={`products-filter-drawer-${definition.key}-min-input`}
          type={isDate ? 'date' : 'number'}
          aria-label={`${definition.label} from`}
          placeholder={lower ? `Min ${lower}` : 'Min'}
          min={isDate ? lower : undefined}
          max={isDate ? upper : undefined}
          value={isDate ? minValue.slice(0, 10) : minValue}
          onChange={(e) => {
            onChange(minKey, e.target.value);
          }}
          className={inputClass}
        />
        <span className="text-muted-foreground text-xs">to</span>
        <Input
          data-testid={`products-filter-drawer-${definition.key}-max-input`}
          type={isDate ? 'date' : 'number'}
          aria-label={`${definition.label} to`}
          placeholder={upper ? `Max ${upper}` : 'Max'}
          min={isDate ? lower : undefined}
          max={isDate ? upper : undefined}
          value={isDate ? maxValue.slice(0, 10) : maxValue}
          onChange={(e) => {
            const v = e.target.value;
            onChange(maxKey, isDate && v ? `${v}${END_OF_DAY}` : v);
          }}
          className={inputClass}
        />
      </div>
    </div>
  );
}

interface BooleanSectionProps {
  definition: CurationFilterDefinition;
  value: boolean;
  onChange: (value: boolean) => void;
  /** E2E `data-testid` prefix; the section is `${testId}-section-<key>`. */
  testId?: string;
}

/** Toggle switch for a `boolean` filter. */
function BooleanSection({
  definition,
  value,
  onChange,
  testId = 'products-filter-drawer',
}: BooleanSectionProps) {
  const id = `bool-${definition.key}`;
  return (
    <div data-testid={`${testId}-section-${definition.key}`} className="space-y-2.5">
      <SectionHeader label={definition.label} count={countActive(value)} />
      <div className="bg-surface-stat-card flex items-center justify-between rounded-xl p-3">
        <Label htmlFor={id} className="cursor-pointer text-sm font-medium">
          {definition.label}
        </Label>
        <Switch
          data-testid={`${testId}-${definition.key}-toggle`}
          id={id}
          checked={value}
          onCheckedChange={onChange}
          className="cursor-pointer"
        />
      </div>
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Filter definitions returned by `useCurationFilters`. Empty array hides
   *  every section and renders a placeholder. */
  definitions: CurationFilterDefinition[];
  /** True while the caller is still deriving `definitions`. Lets a consumer
   *  open the panel immediately and fill it in a moment later, instead of
   *  blocking the open on however long the options take to build. */
  isLoading?: boolean;
  /** Currently applied filters — used to seed the drawer's draft state on
   *  open. */
  appliedFilters: ProductFilterValues;
  /** Called with the committed filter state when the user clicks "Apply
   *  Filters". Empty values are pruned before this callback fires. */
  onApply: (filters: ProductFilterValues) => void;
}

/** Right-side filter drawer for the per-batch Products page. Definition-
 *  driven: it renders one control per backend filter key, so adding a new
 *  filter doesn't require UI changes. */
export function ProductFilterDrawer({
  open,
  onClose,
  definitions,
  isLoading = false,
  appliedFilters,
  onApply,
}: Props) {
  const [draft, setDraft] = useState(appliedFilters);
  // Re-seed the draft on each `open` transition without an effect — using the
  // derived-state pattern keeps the drawer in sync with the parent's applied
  // filters when the user reopens it (and discards any uncommitted edits).
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setDraft(appliedFilters);
  }

  const totalSelected = useMemo(
    () => Object.values(draft).reduce((acc, v) => acc + countActive(v), 0),
    [draft],
  );

  // These three are `useCallback`-stable so the memoised sections below can
  // actually bail out — a fresh handler each render would defeat the memo and
  // re-render every option row in the drawer on each keystroke or tick.
  const handleToggleMulti = useCallback((key: string, value: string) => {
    setDraft((prev) => {
      const current = prev[key];
      const arr = Array.isArray(current) ? current : [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
  }, []);

  const handleSetText = useCallback((key: string, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSetBoolean = useCallback((key: string, value: boolean) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  function handleReset() {
    setDraft({});
  }

  function handleApply() {
    // Prune empty values before committing so consumers don't see blank
    // keys and so the query string stays minimal.
    const pruned: ProductFilterValues = {};
    for (const [key, value] of Object.entries(draft)) {
      if (countActive(value) > 0) pruned[key] = value;
    }
    onApply(pruned);
    onClose();
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent
        data-testid="products-filter-drawer"
        side="right"
        style={{
          top: '1rem',
          bottom: '1rem',
          right: '1rem',
          height: 'auto',
          width: '380px',
          maxWidth: '380px',
        }}
        className="flex flex-col gap-0 rounded-2xl border-0 p-0 shadow-2xl"
      >
        {/* Header — X close button is rendered by SheetContent at absolute top-4 right-4 */}
        <div className="flex items-center gap-2 border-b px-4 py-3 pr-10">
          <FilterIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <SheetTitle
            data-testid="products-filter-drawer-title"
            className="text-base font-semibold"
          >
            Filters
          </SheetTitle>
        </div>

        <CatalogFilterInfoBanner className="mx-4 mt-3" />

        {/* Scrollable filter body */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div
              data-testid="products-filter-drawer-loading"
              className="space-y-4"
              role="status"
              aria-label="Loading filters"
            >
              {SKELETON_SECTIONS.map((key) => (
                <div key={key} className="space-y-2.5">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-28 w-full rounded-xl" />
                </div>
              ))}
            </div>
          ) : definitions.length === 0 ? (
            <p
              data-testid="products-filter-drawer-empty"
              className="text-muted-foreground py-8 text-center text-sm"
            >
              No filters available for this catalog yet.
            </p>
          ) : (
            definitions.map((def, idx) => {
              const value = draft[def.key];
              return (
                <div key={def.key} className="space-y-4">
                  {def.type === 'multi_select' && (
                    <MultiSelectSection
                      definition={def}
                      // `NO_SELECTION`, not a fresh `[]` — a new array each
                      // render would break the section's memo.
                      selected={Array.isArray(value) ? value : NO_SELECTION}
                      onToggle={handleToggleMulti}
                    />
                  )}
                  {(def.type === 'text' || def.type === 'text_contains') && (
                    <TextSection
                      testId="products-filter-drawer"
                      definition={def}
                      value={typeof value === 'string' ? value : ''}
                      onChange={(v) => {
                        handleSetText(def.key, v);
                      }}
                    />
                  )}
                  {(def.type === 'numeric_range' || def.type === 'date_range') && (
                    <RangeSection
                      definition={def}
                      minValue={stringValue(draft[`${def.key}${MIN_SUFFIX}`])}
                      maxValue={stringValue(draft[`${def.key}${MAX_SUFFIX}`])}
                      onChange={handleSetText}
                    />
                  )}
                  {def.type === 'boolean' && (
                    <BooleanSection
                      testId="products-filter-drawer"
                      definition={def}
                      value={value === true}
                      onChange={(v) => {
                        handleSetBoolean(def.key, v);
                      }}
                    />
                  )}
                  {idx < definitions.length - 1 && <div className="border-t border-slate-200" />}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 border-t px-4 py-4">
          <Button
            data-testid="products-filter-drawer-reset-button"
            variant="outline"
            onClick={handleReset}
            className="h-11 flex-1 cursor-pointer rounded-xl bg-white text-sm font-semibold"
          >
            Reset Filters
          </Button>
          <Button
            data-testid="products-filter-drawer-apply-button"
            onClick={handleApply}
            className="h-11 flex-1 cursor-pointer gap-2 rounded-xl text-sm font-semibold"
          >
            Apply Filters
            {totalSelected > 0 && (
              <span
                data-testid="products-filter-drawer-apply-count-badge"
                className="text-primary inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-lg bg-white px-1.5 text-xs font-bold tabular-nums"
              >
                {String(totalSelected)}
              </span>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
